import type { StorePlatform } from '@/types/store';

export const PLATFORM_LABELS: Record<StorePlatform, string> = {
  google_play: 'Google Play',
  app_store: 'App Store',
};

export const PLATFORMS: readonly StorePlatform[] = ['google_play', 'app_store'];

/**
 * Curated per-platform locale codes offered when creating a listing. The stores
 * use slightly different BCP-47 forms (Apple drops the region on some
 * languages), so the sets are kept separate. The backend accepts any string —
 * this list is only guard-rails against typos; tune it to the locales you
 * actually publish.
 */
export const PLATFORM_LOCALES: Record<StorePlatform, readonly string[]> = {
  google_play: [
    'en-US',
    'en-GB',
    'de-DE',
    'fr-FR',
    'es-ES',
    'it-IT',
    'pt-BR',
    'nl-NL',
    'ja-JP',
    'ko-KR',
    'ru-RU',
  ],
  app_store: [
    'en-US',
    'en-GB',
    'de-DE',
    'fr-FR',
    'es-ES',
    'it',
    'pt-BR',
    'nl-NL',
    'ja',
    'ko',
    'ru',
    'zh-Hans',
  ],
};

export const PLATFORM_IMAGE_TYPES: Record<StorePlatform, readonly string[]> = {
  google_play: [
    'phoneScreenshots',
    'sevenInchScreenshots',
    'tenInchScreenshots',
    'tvScreenshots',
    'wearScreenshots',
  ],
  app_store: [
    'APP_IPHONE_67',
    'APP_IPHONE_65',
    'APP_IPHONE_61',
    'APP_IPHONE_55',
    'APP_IPAD_PRO_129',
    'APP_IPAD_PRO_11',
  ],
};

/** Editable listing fields, keyed by the `StoreListing` / `UpsertListingBody` property. */
export type ListingFieldName =
  | 'title'
  | 'shortDescription'
  | 'fullDescription'
  | 'video'
  | 'keywords'
  | 'whatsNew'
  | 'promotionalText'
  | 'marketingUrl'
  | 'supportUrl';

export interface ListingFieldSpec {
  name: ListingFieldName;
  label: string;
  /** `input` = one-line text, `textarea` = multi-line, `url` = one-line + URL validation. */
  kind: 'input' | 'textarea' | 'url';
  /** Character cap mirroring `UpsertListingDto` (client guard = server rule). */
  maxLength?: number;
  placeholder?: string;
  help?: string;
}

/**
 * Which editable fields each platform exposes, in display order. Drives both the
 * edit form and the listing table's summary column. `fullDescription` is the
 * shared main body (Google `fullDescription` / App Store `description`).
 */
export const PLATFORM_FIELD_SPECS: Record<StorePlatform, readonly ListingFieldSpec[]> = {
  google_play: [
    { name: 'title', label: 'Title', kind: 'input', maxLength: 30, placeholder: 'Planet Proxy' },
    {
      name: 'shortDescription',
      label: 'Short description',
      kind: 'input',
      maxLength: 80,
      placeholder: 'Fast, private VPN',
    },
    { name: 'fullDescription', label: 'Full description', kind: 'textarea', maxLength: 4000 },
    {
      name: 'video',
      label: 'Promo video URL',
      kind: 'url',
      placeholder: 'https://youtu.be/…',
    },
  ],
  app_store: [
    { name: 'fullDescription', label: 'Description', kind: 'textarea', maxLength: 4000 },
    {
      name: 'keywords',
      label: 'Keywords (comma-separated)',
      kind: 'input',
      maxLength: 100,
      placeholder: 'vpn,privacy,proxy',
    },
    { name: 'whatsNew', label: "What's new", kind: 'textarea', maxLength: 4000 },
    {
      name: 'promotionalText',
      label: 'Promotional text',
      kind: 'textarea',
      maxLength: 170,
      help: 'The one App Store field that goes live without a new version / review.',
    },
    {
      name: 'marketingUrl',
      label: 'Marketing URL',
      kind: 'url',
      placeholder: 'https://getpocketvpn.com',
    },
    {
      name: 'supportUrl',
      label: 'Support URL',
      kind: 'url',
      placeholder: 'https://getpocketvpn.com/support',
    },
  ],
};

/** Shared class for native `<select>` controls (mirrors add-server-dialog). */
export const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-border bg-card px-3 py-1 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40';
